import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Volume2, VolumeX, Mic, MicOff, Speaker } from 'lucide-react';
import { useSettings } from '../../hooks/useSettings';
import { toast } from 'sonner';
import { getEffectiveVolume, playSystemSound } from '../../lib/sound';

export const SoundSettings: React.FC = () => {
  const defaultSound = {
    masterVolume: 75,
    systemVolume: 60,
    micVolume: 50,
    isMuted: false,
    micMuted: false,
    outputDevice: 'speakers',
    inputDevice: 'microphone',
    soundScheme: 'default',
    startupEnabled: true,
    spatialAudio: false,
    noiseCancellation: false,
    audioEnhancements: false,
    startupSoundUrl: '/os/assets/audio/startup.mp3',
  };
  const { settings, isLoaded, updateSetting, resetSettings } = useSettings('sound', defaultSound);

  return (
    <div className="h-full p-6 bg-background overflow-auto">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">Sound Settings</h1>
          <p className="text-muted-foreground">
            Configure audio devices and sound preferences
          </p>
        </div>

        {/* Volume Controls */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Volume2 size={20} />
              Volume Controls
            </CardTitle>
            <CardDescription>
              Adjust system audio levels
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="master-volume" className="flex items-center gap-2">
                  {settings.isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  Master Volume
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground w-12 text-right">
                    {settings.isMuted ? '0' : settings.masterVolume}%
                  </span>
                  <Switch
                    checked={!settings.isMuted}
                    onCheckedChange={(checked) => updateSetting('isMuted', !checked)}
                  />
                </div>
              </div>
              <Slider
                id="master-volume"
                value={settings.isMuted ? [0] : [settings.masterVolume]}
                onValueChange={([v]) => updateSetting('masterVolume', v)}
                max={100}
                step={1}
                className="w-full"
                disabled={settings.isMuted}
              />
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="system-volume">System Sounds</Label>
                <span className="text-sm text-muted-foreground">
                  {settings.systemVolume}%
                </span>
              </div>
              <Slider
                id="system-volume"
                value={[settings.systemVolume]}
                onValueChange={([v]) => updateSetting('systemVolume', v)}
                max={100}
                step={1}
                className="w-full"
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="mic-volume" className="flex items-center gap-2">
                  {settings.micMuted ? <MicOff size={16} /> : <Mic size={16} />}
                  Microphone
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground w-12 text-right">
                    {settings.micMuted ? '0' : settings.micVolume}%
                  </span>
                  <Switch
                    checked={!settings.micMuted}
                    onCheckedChange={(checked) => updateSetting('micMuted', !checked)}
                  />
                </div>
              </div>
              <Slider
                id="mic-volume"
                value={settings.micMuted ? [0] : [settings.micVolume]}
                onValueChange={([v]) => updateSetting('micVolume', v)}
                max={100}
                step={1}
                className="w-full"
                disabled={settings.micMuted}
              />
            </div>
          </CardContent>
        </Card>

        {/* Audio Devices */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Speaker size={20} />
              Audio Devices
            </CardTitle>
            <CardDescription>
              Select and configure audio devices
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="output-device">Output Device</Label>
              <Select value={settings.outputDevice} onValueChange={(v) => {
                updateSetting('outputDevice', v);
                toast.success(`Output device set to ${v}`);
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select output device" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="speakers">Speakers (Built-in)</SelectItem>
                  <SelectItem value="headphones">Headphones</SelectItem>
                  <SelectItem value="bluetooth">Bluetooth Audio</SelectItem>
                  <SelectItem value="hdmi">HDMI Audio</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="input-device">Input Device</Label>
              <Select value={settings.inputDevice} onValueChange={(v) => {
                updateSetting('inputDevice', v);
                toast.success(`Input device set to ${v}`);
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select input device" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="microphone">Microphone (Built-in)</SelectItem>
                  <SelectItem value="headset">Headset Microphone</SelectItem>
                  <SelectItem value="usb">USB Microphone</SelectItem>
                  <SelectItem value="line-in">Line In</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Sound Scheme */}
        <Card>
          <CardHeader>
            <CardTitle>Sound Scheme</CardTitle>
            <CardDescription>
              Choose system sound effects
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sound-scheme">Sound Scheme</Label>
              <Select value={settings.soundScheme} onValueChange={(v) => updateSetting('soundScheme', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select sound scheme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="minimal">Minimal</SelectItem>
                  <SelectItem value="retro">Retro</SelectItem>
                  <SelectItem value="none">No Sounds</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const vol = getEffectiveVolume(settings.masterVolume, settings.systemVolume, settings.isMuted);
                  playSystemSound(settings.soundScheme as any, vol, settings.isMuted);
                  toast.success('Playing test system sound');
                }}
              >
                Test System Sounds
              </Button>
              <div className="flex items-center justify-between rounded-md border p-2">
                <Label htmlFor="startup-sound" className="text-sm">Startup Sound</Label>
                <Switch
                  id="startup-sound"
                  checked={!!settings.startupEnabled}
                  onCheckedChange={(checked) => updateSetting('startupEnabled', checked)}
                />
              </div>
            </div>
            <div className="pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  updateSetting('startupEnabled', false);
                  toast.success('Startup sound disabled');
                }}
              >
                Disable Startup Sound
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Advanced Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Advanced Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="spatial-audio">Spatial Audio</Label>
              <Switch id="spatial-audio" checked={!!settings.spatialAudio} onCheckedChange={(c) => updateSetting('spatialAudio', c)} />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="noise-cancellation">Noise Cancellation</Label>
              <Switch id="noise-cancellation" checked={!!settings.noiseCancellation} onCheckedChange={(c) => updateSetting('noiseCancellation', c)} />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="audio-enhancements">Audio Enhancements</Label>
              <Switch id="audio-enhancements" checked={!!settings.audioEnhancements} onCheckedChange={(c) => updateSetting('audioEnhancements', c)} />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            onClick={() => {
              resetSettings();
              toast.success('Sound settings reset to defaults');
            }}
          >
            Reset to Defaults
          </Button>
          <Button
            onClick={() => {
              // Settings save on change; this is a friendly confirmation
              toast.success('Sound settings applied');
            }}
          >
            Apply Changes
          </Button>
        </div>
      </div>
    </div>
  );
};